import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Button, lighten, Typography } from '@mui/material';
import {
    BoxSkeleton,
    ConfirmationModal,
    FlexyTabs,
    SkeletonLoader,
    TabStructure,
    useL2DTheme,
    useModal,
} from '@europrocurement/flexy-components';
import {
    type AddArticleParamsType,
    ArticleCategory,
    CATEGORY_BODACC,
    CATEGORY_GREFFE,
    CATEGORY_LEGAL_ANNOUCEMENT,
    CATEGORY_TAXES,
    type DossierArticleWithInvoice,
    PROCESS_STATUS_FLAG_ONGOING,
    REFRESH_CURRENT_DOSSIER_CTX,
    useCurrentDossierCtx,
    useCurrentDossierCtxDispatch,
    useDossierArticle,
    useDossierArticleContext,
    useDossierProcesses,
    useDossierRoutingContext,
    VALIDATOR_TYPE_GREFFE,
} from '@europrocurement/l2d-modules';
import { FaOptionIcon } from '@europrocurement/l2d-icons';
import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router';
import DossierFormaliteProcesses from '../DossierFormaliteProcesses/DossierFormaliteProcesses';
import DossierLegalAnnoucements from '../DossierLegalAnnoucements/DossierLegalAnnoucements';
import DossierBodaccProcesses from '../DossierBodaccProcesses/DossierBodaccProcesses';
import DossierImpotProcesses from '../DossierImpotProcesses/DossierImpotProcesses';
import DossierArticlesProcesses from '../DossierArticlesProcesses/DossierArticlesProcesses';
import DossierProcessesFooter from '../DossierProcessesFooter/DossierProcessesFooter';
import AddProcessForm from '../AddProcessForm/AddProcessForm';
import { AddTaxFormData } from '../AddTaxForm/AddTaxForm';
import DossierArticleProcessStatus from '../DossierArticleProcessStatus/DossierArticleProcessStatus';
import DossierProcessesTabTitle from './DossierProcessesTabTitle';

const DossierProcesses: React.FunctionComponent = function () {
    const { dossier } = useCurrentDossierCtx();
    const dispatch = useCurrentDossierCtxDispatch();

    const refreshDossier = useCallback(() => {
        if (dispatch) {
            dispatch({ type: REFRESH_CURRENT_DOSSIER_CTX });
        }
    }, [dispatch]);

    const { addArticle, getArticlesWithInvoicesByDossier } = useDossierArticle();
    const {
        createValidatorProcess,
        canCreateValidatorProcess,
        canAddFormality,
        getCurrentProcessOfType,
    } = useDossierProcesses();
    const { getAddFormalityRoute } = useDossierRoutingContext();
    const { palette } = useL2DTheme();
    const { modalActions } = useModal();
    const { articleCategories } = useDossierArticleContext();
    const navigate = useNavigate();

    const [articlesLoaded, setArticlesLoaded] = useState<boolean>(false);
    const [articles, setArticles] = useState<Array<DossierArticleWithInvoice>>([]);
    const [blockDisplayed, setBlockDisplayed] = useState<Array<number>>([]);
    const [activeTab, setActiveTab] = useState<string | undefined>(undefined);

    const refreshArticles = async (dossierId: number) => {
        setArticlesLoaded(false);

        try {
            const response = await getArticlesWithInvoicesByDossier(dossierId);

            setArticles(response);
        } catch (e) {
            console.error(e);
        } finally {
            setBlockDisplayed([]);
            setArticlesLoaded(true);
        }
    };

    useEffect(() => {
        if (dossier?.id) {
            refreshArticles(dossier.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier?.id, dossier?.validationProcesses]);

    const findArticlesByCategory = (categoryId: number): Array<DossierArticleWithInvoice> => {
        if (!articlesLoaded) {
            return [];
        }

        return articles.filter((article) => article.categoryId === categoryId);
    };

    const findBodaccArticles = (): Array<DossierArticleWithInvoice> => {
        const bodaccArticles = findArticlesByCategory(CATEGORY_BODACC);

        return bodaccArticles.filter((res) => res.article.rubArticle === 'FRN');
    };

    const onSubmitProcess = async (formData: AddTaxFormData) => {
        if (!dossier) return;
        try {
            const articleId = await addArticle({
                prestationId: formData.prestationId,
                label: formData.title,
                factArticleId: formData.articleId,
            } as AddArticleParamsType);

            const process = await createValidatorProcess(dossier.id as number, [articleId]);
            if (process) {
                refreshDossier();
            }
        } catch (e) {
            console.error(e);
            enqueueSnackbar("Une erreur est survenue lors de l'ajout de votre démarche", {
                variant: 'error',
            });
        } finally {
            modalActions.close();
        }
    };

    const getMinSortOrderInCategory = (category: ArticleCategory): number => {
        const articlesFromCategory =
            category.id === CATEGORY_BODACC
                ? findBodaccArticles()
                : findArticlesByCategory(category.id);

        return articlesFromCategory.reduce(
            (minRang, current) => (current.rang < minRang ? current.rang : minRang),
            999,
        );
    };

    const blocks = useMemo(
        () =>
            [...articleCategories]
                .sort((categoryA, categoryB) => {
                    const minArticlesByCategoryA = getMinSortOrderInCategory(categoryA);
                    const minArticlesByCategoryB = getMinSortOrderInCategory(categoryB);

                    return minArticlesByCategoryA - minArticlesByCategoryB;
                })
                .filter((articleCategory) => articleCategory.validatorTypeId)
                .map((articleCategory) => {
                    let checked = blockDisplayed.includes(articleCategory.id);

                    if (articleCategory.id === CATEGORY_GREFFE) {
                        checked = !!(
                            checked ||
                            (dossier &&
                                dossier.validationProcesses &&
                                dossier.validationProcesses.filter(
                                    (process) => process.validatorTypeId === VALIDATOR_TYPE_GREFFE,
                                ).length > 0)
                        );
                    } else if (articleCategory.id === CATEGORY_BODACC) {
                        checked = checked || findBodaccArticles().length > 0;
                    } else {
                        checked = checked || findArticlesByCategory(articleCategory.id).length > 0;
                    }

                    return {
                        id: articleCategory.id,
                        label: articleCategory.label,
                        validatorTypeId: articleCategory.validatorTypeId,
                        checked,
                    };
                }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [articleCategories, blockDisplayed, findBodaccArticles, findArticlesByCategory],
    );

    const isCategoryVisible = (idCategory: number) => {
        const category = blocks.find((c) => c.id === idCategory);

        return category?.checked || false;
    };

    const showAddProcessModal = (categoryId: number) => {
        if (!dossier) return;
        modalActions.call(
            <AddProcessForm
                categoryId={categoryId}
                dossier={dossier}
                onSubmit={onSubmitProcess}
                onCancel={() => modalActions.close()}
            />,
        );
    };

    const navigateToParcours = () => {
        const path = getAddFormalityRoute(dossier?.id as number);
        const invoiced = dossier?.invoiced as boolean;
        const processes = dossier?.validationProcesses || [];
        const hasNotOnGoingProcess = processes.find(
            (p) => p.statusFlag !== PROCESS_STATUS_FLAG_ONGOING,
        );

        if (invoiced || (!hasNotOnGoingProcess && processes.length > 0)) {
            modalActions.call(
                <ConfirmationModal
                    messages={{
                        question: 'Le traitement de ce dossier a déjà débuté.',
                        detail: {
                            message:
                                "Un nouveau dossier sera créé lors de l'ajout d'une formalité.",
                            severity: 'info',
                        },
                        validation: "J'ai compris",
                        annulation: 'Annuler',
                    }}
                    actionOnCancellation={() => modalActions.close()}
                    actionOnValidation={() => {
                        if (path) {
                            navigate(path);
                            modalActions.close();
                        }
                    }}
                    color={palette.primary}
                    isFullWidth
                />,
            );

            return;
        }

        if (path) {
            navigate(path);
        }
    };

    if (!dossier) {
        return null;
    }

    const tabs: TabStructure[] = blocks
        .filter((category) => isCategoryVisible(category.id))
        .map((category) => {
            if (category.id === CATEGORY_GREFFE) {
                const greffeProcess = getCurrentProcessOfType(dossier, VALIDATOR_TYPE_GREFFE);

                return {
                    tabName: `category-${category.id}`,
                    tabTitle: (
                        <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            {category.label}

                            <DossierArticleProcessStatus
                                statusFlag={greffeProcess?.statusFlag ?? 100}
                                statusLabel={greffeProcess?.statusLabel ?? ''}
                            />
                        </Box>
                    ),
                    tabPanel: <DossierFormaliteProcesses />,
                };
            }

            if (category.id === CATEGORY_LEGAL_ANNOUCEMENT) {
                const legalArticles = findArticlesByCategory(category.id);

                return {
                    tabName: `category-${category.id}`,
                    tabTitle: (
                        <DossierProcessesTabTitle
                            category={category}
                            dossier={dossier}
                            dossierArticles={legalArticles}
                        />
                    ),
                    tabPanel: <DossierLegalAnnoucements articles={legalArticles} />,
                };
            }

            if (category.id === CATEGORY_BODACC) {
                const bodaccArticles = findBodaccArticles();

                return {
                    tabName: `category-${category.id}`,
                    tabTitle: (
                        <DossierProcessesTabTitle
                            category={category}
                            dossier={dossier}
                            dossierArticles={bodaccArticles}
                        />
                    ),
                    tabPanel: <DossierBodaccProcesses articles={bodaccArticles} />,
                };
            }

            if (category.id === CATEGORY_TAXES) {
                const taxArticles = findArticlesByCategory(category.id);

                return {
                    tabName: `category-${category.id}`,
                    tabTitle: (
                        <DossierProcessesTabTitle
                            category={category}
                            dossier={dossier}
                            dossierArticles={taxArticles}
                        />
                    ),
                    tabPanel: <DossierImpotProcesses articles={taxArticles} />,
                };
            }

            const footer = canCreateValidatorProcess(dossier, category.validatorTypeId) ? (
                <DossierProcessesFooter
                    key={category.label}
                    handleAddItem={() => showAddProcessModal(category.id)}
                    addItemTextBtn="Ajouter un enregistrement"
                    canAddItem
                />
            ) : null;

            const processArticles = findArticlesByCategory(category.id);

            return {
                tabName: `category-${category.id}`,
                tabTitle: (
                    <DossierProcessesTabTitle
                        category={category}
                        dossier={dossier}
                        dossierArticles={processArticles}
                    />
                ),
                tabPanel: (
                    <DossierArticlesProcesses
                        validatorTypeId={category.validatorTypeId}
                        footer={footer}
                        articles={processArticles}
                    />
                ),
            };
        });

    return (
        <SkeletonLoader
            isLoading={!articlesLoaded}
            containerSx={{
                marginTop: '10px',
                marginBottom: '10px',
            }}
            skeleton={
                <BoxSkeleton
                    sx={{
                        height: '250px',
                    }}
                />
            }
        >
            <Box
                sx={{
                    padding: '0 0 16px 0',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '16px',
                    }}
                >
                    <Typography
                        variant="l2dh6"
                        data-testid="title-dossier-process"
                    >
                        Démarches
                    </Typography>
                    {canAddFormality(dossier) && (
                        <Button
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0 8px',
                                color: lighten(palette.secondary.main, 0.3),
                            }}
                            variant="text"
                            onClick={navigateToParcours}
                        >
                            <FaOptionIcon
                                icon={['fas', 'plus']}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    lineHeight: 0,
                                }}
                            />
                            <Typography variant="l2dcaption">Ajouter une formalité</Typography>
                        </Button>
                    )}
                </Box>

                {tabs.length > 0 && (
                    <FlexyTabs
                        tabs={tabs}
                        scrollbar={false}
                        indicatorColor="primary"
                        useLocalState
                        inAccordion
                        activeTabName={activeTab}
                        onTabChange={setActiveTab}
                    />
                )}
            </Box>
        </SkeletonLoader>
    );
};

export default DossierProcesses;
